import React from "react";
import {
  Feature,
  Button,
  SwiperCarousel,
  IntersectionAnimation,
  SliceConfig,
  TransparentVideo
} from "~components";
import cn from "classnames";
import { PortableText } from "@portabletext/react";
import { GatsbyImage } from "gatsby-plugin-image";
import * as styles from "./styles.module.scss";

const customComponents = {
  block: {
    h3: ({ children }) => <h3 className="h4">{children}</h3>,
    normal: ({ children }) => <p className="b2">{children}</p>
  }
};

const MediaContainerComponent = ({
  mediaType,
  images,
  video,
  transparentVideo
}) => {
  if (mediaType === `image` && images?.length > 1) {
    return (
      <div className={styles.carouselContainer}>
        <SwiperCarousel fullSpace className={styles.carousel}>
          {images.map((image, i) => (
            <div key={i} className={styles.carouselItem}>
              <GatsbyImage
                image={image?.asset?.gatsbyImageData}
                alt={image?.altText || ``}
                style={{ height: `100%`, width: `100%` }}
              />
            </div>
          ))}
        </SwiperCarousel>
      </div>
    );
  }
  if (mediaType === `image`) {
    return (
      <GatsbyImage
        image={images?.[0]?.asset?.gatsbyImageData}
        alt={images?.[0]?.altText || ``}
        style={{ height: `100%`, width: `100%` }}
      />
    );
  }
  if (mediaType === `transparentVideo`) {
    return (
      <div className={styles.video}>
        <TransparentVideo
          webM={transparentVideo?.webM}
          mov={transparentVideo?.mov}
        />
      </div>
    );
  }
  if (mediaType === `video`) {
    return (
      <video
        muted
        autoPlay
        loop
        playsInline
        className={styles.video}
        src={video?.asset?.url}
      />
    );
  }
  return null;
};

const MediaTextCard = ({
  showButton,
  button,
  features,
  heading,
  images,
  mediaType,
  _rawTextContent: textContent,
  video,
  transparentVideo,
  mediaOnRight,
  headerTextArray, // for PDP
  textBackground,
  textBlockBackground,
  sliceConfig
}) => {
  const buttonUrl = () => {
    switch (button.linkTo) {
      case `page`:
        return `/${button.pageLink.slug.current}`;
      case `product`:
        return `/products/${button.productLink.store.slug.current}`;
      case `external`:
        return button.externalLink;
      default:
        return null;
    }
  };

  return (
    <SliceConfig config={sliceConfig}>
      <div
        className={cn(styles.flexContainer, {
          [styles.mediaOnRight]: mediaOnRight
        })}
      >
        <div
          className={cn(styles.mediaContainer, {
            [styles.square]: images?.length > 1
          })}
        >
          <MediaContainerComponent
            mediaType={mediaType}
            images={images}
            video={video}
            transparentVideo={transparentVideo}
          />
        </div>
        <div
          className={styles.textBlock}
          style={{
            background: textBlockBackground?.hex
          }}
        >
          <div
            className={styles.textContainer}
            style={{ background: textBackground?.hex }}
          >
            {heading && (
              <IntersectionAnimation
                className={styles.heading}
                animation="fadeGrow"
                delay={100}
              >
                <h2 className="h2">{heading}</h2>
              </IntersectionAnimation>
            )}
            <IntersectionAnimation animation="fadeGrow" delay={200}>
              <PortableText value={textContent} components={customComponents} />
            </IntersectionAnimation>
            <IntersectionAnimation animation="fadeGrow" delay={300}>
              {headerTextArray?.map((textBlock, i) => (
                <div key={i} className={styles.textArray}>
                  <h3 className="h4">{textBlock.header}</h3>
                  <p className="b1">{textBlock.text}</p>
                </div>
              ))}
              {features?.[0] && (
                <div className={styles.features}>
                  {features.map((feature, i) => (
                    <div className={styles.feature} key={i}>
                      <Feature {...feature} />
                    </div>
                  ))}
                </div>
              )}

              {showButton && button?.text && (
                <Button
                  light={button?.theme === `light`}
                  variant={button?.variant}
                  to={button.linkTo !== `external` ? buttonUrl() : null}
                  href={button.linkTo === `external` ? buttonUrl() : null}
                >
                  {button?.text}
                </Button>
              )}
            </IntersectionAnimation>
          </div>
        </div>
      </div>
    </SliceConfig>
  );
};

export default MediaTextCard;
